import { gql } from "graphql-request";
import { capitalizeFirstLetter } from "@/lib/utils";
import { createGraphQLClient } from "@/services/graphql.service";
import type { FilterInput } from "@/services/entity-instance.service";

/**
 * Registers (or updates) an `upsert<Entity>Subscription` with the backend, which
 * synchronously hands it to the websocket-service. The client owns the
 * `connectionId` so every subscription shares one websocket; re-upserting with
 * the same `id` updates a subscription in place (e.g. after a filter change).
 *
 * The returned `webserviceUrl` already includes the connectionId
 * (`WEBSOCKET_PUBLIC_URL/<connectionId>`) and is what the browser opens.
 */
export interface UpsertSubscriptionArgs {
  entityName: string;
  /** GraphQL selection body spliced into `fields { ... }` — the per-event projection. */
  fieldsSelection: string;
  filter?: FilterInput;
  /** Reuse an existing subscription id to update it in place. */
  id?: string;
  /** Shared connection all subscriptions live on. */
  connectionId: string;
  onCreate?: boolean;
  onUpdate?: boolean;
  onDelete?: boolean;
}

export interface UpsertSubscriptionResult {
  webserviceUrl: string;
  connectionId: string;
  id: string;
}

export const upsertEntitySubscription = async ({
  entityName,
  fieldsSelection,
  filter,
  id,
  connectionId,
  onCreate = false,
  onUpdate = false,
  onDelete = false,
}: UpsertSubscriptionArgs): Promise<UpsertSubscriptionResult> => {
  const capitalized = capitalizeFirstLetter(entityName);
  const mutationField = `upsert${capitalized}Subscription`;

  const mutation = gql`
    mutation ${mutationField}($subscription: SubscriptionInput, $filter: FilterInput) {
      ${mutationField}(subscription: $subscription, filter: $filter) {
        subscription {
          webserviceUrl
          connectionId
          id
        }
        fields {
          ${fieldsSelection}
        }
      }
    }
  `;

  const subscription: Record<string, unknown> = {
    connectionId,
    onCreate,
    onUpdate,
    onDelete,
  };
  if (id) subscription.id = id;

  const client = createGraphQLClient({ endpointName: `${entityName}Subscribe` });

  const result = (await client.request(mutation, { subscription, filter })) as {
    [key: string]: { subscription: UpsertSubscriptionResult };
  };

  return result[mutationField].subscription;
};

const DELETE_SUBSCRIPTION = gql`
  mutation deleteSubscription($id: ID!, $connectionId: String!) {
    deleteSubscription(id: $id, connectionId: $connectionId) {
      data {
        success
      }
    }
  }
`;

/** Idempotent: deleting an unknown or already-deleted subscription succeeds. */
export const deleteEntitySubscription = async ({
  id,
  connectionId,
}: {
  id: string;
  connectionId: string;
}): Promise<void> => {
  const client = createGraphQLClient({ endpointName: "deleteSubscription" });
  await client.request(DELETE_SUBSCRIPTION, { id, connectionId });
};

/** Parse a websocket event operation like `entity.create.Customer`. */
export type EntityEventKind = "create" | "update" | "delete";

export const parseEntityEventOperation = (
  operation: string,
): { kind: EntityEventKind; entityName: string } | null => {
  const parts = operation.split(".");
  if (parts.length < 3 || parts[0] !== "entity") return null;
  const kind = parts[1];
  if (kind !== "create" && kind !== "update" && kind !== "delete") return null;
  return { kind, entityName: parts.slice(2).join(".") };
};
